import { useState } from 'react';
import PropTypes from 'prop-types';
import Button from '../common/Button';
import { formatDate } from '../../utils/formatters';
import './NotesEditor.css';

const MAX_BODY_LENGTH = 5000;

/**
 * NotesEditor — inline note management for the customer detail view.
 * Handles add, edit, and delete for CompanyNote records.
 */
const NotesEditor = ({
  companyId,
  notes,
  onAdd,
  onEdit,
  onDelete,
  isSaving,
}) => {
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState('');
  const [draftError, setDraftError] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(null);

  const openAdd = () => {
    setDraft('');
    setDraftError('');
    setEditingId(null);
    setShowForm(true);
  };

  const openEdit = (note) => {
    setDraft(note.body);
    setDraftError('');
    setEditingId(note.id);
    setShowForm(false);
  };

  const closeForm = () => {
    setShowForm(false);
    setEditingId(null);
    setDraft('');
    setDraftError('');
  };

  const handleDraftChange = (e) => {
    const val = e.target.value;
    setDraft(val);
    setDraftError(val.length > MAX_BODY_LENGTH
      ? `Note too long (max ${MAX_BODY_LENGTH} characters).`
      : '');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (draftError || !draft.trim()) return;

    try {
      if (editingId) {
        await onEdit(editingId, draft.trim());
      } else {
        await onAdd(companyId, draft.trim());
      }
      closeForm();
    } catch {
      // parent surfaces error via alert
    }
  };

  const handleDelete = async (id) => {
    try {
      await onDelete(id);
      setConfirmDelete(null);
    } catch {
      setConfirmDelete(null);
    }
  };

  return (
    <div className="notes-editor">

      {/* ── Existing note cards ──────────────────────────────── */}
      {notes.length === 0 && !showForm && (
        <p className="notes-editor__empty">No notes yet.</p>
      )}

      {notes.map((note) => (
        <div key={note.id} className="notes-editor__card">
          {editingId === note.id ? (

            /* ── Inline edit form ─────────────────────── */
            <form className="notes-editor__form" onSubmit={handleSubmit} noValidate>
              <textarea
                className={`notes-editor__textarea${draftError ? ' notes-editor__textarea--error' : ''}`}
                value={draft}
                onChange={handleDraftChange}
                rows={4}
                autoFocus
              />
              {draftError && <span className="notes-editor__error">{draftError}</span>}
              <div className="notes-editor__form-meta">
                <span className="notes-editor__char-count">{draft.length} / {MAX_BODY_LENGTH}</span>
              </div>
              <div className="notes-editor__form-actions">
                <Button type="button" variant="secondary" onClick={closeForm} disabled={isSaving}>
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="primary"
                  disabled={isSaving || !!draftError || !draft.trim() || draft.trim() === note.body}
                >
                  {isSaving ? 'Saving...' : 'Save'}
                </Button>
              </div>
            </form>

          ) : confirmDelete === note.id ? (

            /* ── Delete confirmation ──────────────────── */
            <div className="notes-editor__confirm">
              <span className="notes-editor__confirm-text">Delete this note?</span>
              <div className="notes-editor__confirm-actions">
                <Button type="button" variant="secondary" onClick={() => setConfirmDelete(null)} disabled={isSaving}>
                  Cancel
                </Button>
                <Button type="button" variant="danger" onClick={() => handleDelete(note.id)} disabled={isSaving}>
                  {isSaving ? 'Deleting...' : 'Delete'}
                </Button>
              </div>
            </div>

          ) : (

            /* ── Read view ────────────────────────────── */
            <>
              <p className="notes-editor__body">{note.body}</p>
              <div className="notes-editor__card-footer">
                <span className="notes-editor__meta">
                  {note.created_by_name
                    ? `${note.created_by_name} · `
                    : ''}
                  {formatDate(note.created_at)}
                  {note.updated_at?.slice(0, 19) !== note.created_at?.slice(0, 19) && (
                    <span className="notes-editor__edited"> (edited)</span>
                  )}
                </span>
                <div className="notes-editor__card-actions">
                  <button
                    type="button"
                    className="notes-editor__action notes-editor__action--edit"
                    onClick={() => openEdit(note)}
                    disabled={isSaving || editingId !== null || showForm}
                  >
                    Edit
                  </button>
                  <button
                    type="button"
                    className="notes-editor__action notes-editor__action--delete"
                    onClick={() => setConfirmDelete(note.id)}
                    disabled={isSaving || editingId !== null || showForm}
                  >
                    Delete
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      ))}

      {/* ── New note form / add button ────────────────────────── */}
      {showForm ? (
        <form className="notes-editor__form notes-editor__form--new" onSubmit={handleSubmit} noValidate>
          <textarea
            className={`notes-editor__textarea${draftError ? ' notes-editor__textarea--error' : ''}`}
            value={draft}
            onChange={handleDraftChange}
            rows={4}
            placeholder="Write a note..."
            autoFocus
          />
          {draftError && <span className="notes-editor__error">{draftError}</span>}
          <div className="notes-editor__form-meta">
            <span className="notes-editor__char-count">{draft.length} / {MAX_BODY_LENGTH}</span>
          </div>
          <div className="notes-editor__form-actions">
            <Button type="button" variant="secondary" onClick={closeForm} disabled={isSaving}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={isSaving || !!draftError || !draft.trim()}
            >
              {isSaving ? 'Saving...' : 'Add Note'}
            </Button>
          </div>
        </form>
      ) : (
        !editingId && (
          <button type="button" className="notes-editor__add-btn" onClick={openAdd}>
            <span className="notes-editor__add-icon" aria-hidden="true">+</span>
            Add Note
          </button>
        )
      )}
    </div>
  );
};

NotesEditor.propTypes = {
  companyId: PropTypes.number.isRequired,
  notes: PropTypes.array.isRequired,
  onAdd: PropTypes.func.isRequired,
  onEdit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  isSaving: PropTypes.bool,
};

export default NotesEditor;
